import { useState } from "react"
import styles from "./status-update-modal.module.css"
import { useTheme } from "@/components/context/ThemeContext"

export default function StatusUpdateModal({ tournament, onClose, onUpdate }) {
  const { theme } = useTheme();
  const [selectedStatus, setSelectedStatus] = useState(tournament.status)
  const [showConfirm, setShowConfirm] = useState(false)

  const statusOptions = [
    { value: "upcoming", label: "Upcoming", description: "Tournament hasn't started yet", color: "#7c3aed" },
    {
      value: "registration_open",
      label: "Registration Open",
      description: "Players can register now",
      color: "#00d4ff",
    },
    {
      value: "registration_closed",
      label: "Registration Closed",
      description: "Registration period ended",
      color: "#ff6b6b",
    },
    { value: "ongoing", label: "Ongoing", description: "Tournament is currently happening", color: "#00ff00" },
    { value: "completed", label: "Completed", description: "Tournament has finished", color: "#808080" },
    { value: "cancelled", label: "Cancelled", description: "Tournament has been cancelled", color: "#ff4444" },
  ]

  const getStatusBadge = (status) => {
    return statusOptions.find((opt) => opt.value === status) || statusOptions[0]
  }

  const currentStatusBadge = getStatusBadge(tournament.status)
  const newStatusBadge = getStatusBadge(selectedStatus)

  const handleConfirm = () => {
    if (selectedStatus !== tournament.status) {
      onUpdate({ status: selectedStatus })
      onClose()
    } else {
      onClose()
    }
  }

  return (
    <div className={`${styles.modalOverlay} ${theme==="dark" ? "dark" : ""}`} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Update Tournament Status</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        {!showConfirm ? (
          <div className={styles.modalContent}>
            <div className={styles.currentStatus}>
              <p className={styles.label}>Current Status</p>
              <div
                className={styles.statusBadgeDisplay}
                style={{ borderColor: currentStatusBadge.color, color: currentStatusBadge.color }}
              >
                <div className={styles.statusDot} style={{ backgroundColor: currentStatusBadge.color }} />
                <span>{currentStatusBadge.label}</span>
              </div>
            </div>

            <div className={styles.statusGrid}>
              <p className={styles.label}>Select New Status</p>
              <div className={styles.statusOptions}>
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`${styles.statusOption} ${selectedStatus === option.value ? styles.selected : ""}`}
                    onClick={() => setSelectedStatus(option.value)}
                    style={{
                      borderColor: selectedStatus === option.value ? option.color : "rgba(0, 212, 255, 0.2)",
                      backgroundColor:
                        selectedStatus === option.value ? `${option.color}15` : "rgba(0, 212, 255, 0.05)",
                    }}
                  >
                    <div className={styles.statusOptionContent}>
                      <div className={styles.statusDotSmall} style={{ backgroundColor: option.color }} />
                      <div>
                        <p className={styles.statusName}>{option.label}</p>
                        <p className={styles.statusDescription}>{option.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.previewSection}>
              <p className={styles.label}>Preview</p>
              <div className={styles.previewBox}>
                <div>
                  <span className={styles.previewLabel}>From:</span>
                  <div className={styles.previewBadge} style={{ color: currentStatusBadge.color }}>
                    {currentStatusBadge.label}
                  </div>
                </div>
                <span className={styles.arrow}>→</span>
                <div>
                  <span className={styles.previewLabel}>To:</span>
                  <div className={styles.previewBadge} style={{ color: newStatusBadge.color }}>
                    {newStatusBadge.label}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.modalButtons}>
              <button className={styles.cancelButton} onClick={onClose}>
                Cancel
              </button>
              <button
                className={styles.updateButton}
                onClick={() => setShowConfirm(true)}
                disabled={selectedStatus === tournament.status}
              >
                Continue to Confirm
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.confirmContent}>
            <div className={styles.confirmIcon}>⚠️</div>
            <h3>Confirm Status Change</h3>
            <p>
              You are about to change the tournament status from <strong>{currentStatusBadge.label}</strong> to{" "}
              <strong>{newStatusBadge.label}</strong>
            </p>
            <p className={styles.confirmWarning}>This action may notify participants about the status change.</p>

            <div className={styles.confirmButtons}>
              <button className={styles.cancelButton} onClick={() => setShowConfirm(false)}>
                Go Back
              </button>
              <button className={styles.confirmButton} onClick={handleConfirm}>
                Confirm Status Change
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
